import { useEffect, useState } from "react";
import { cn } from "@/core/utils/utils";

interface PayrollPeriodTimelineLineProps {
  periodCount: number;
  firstTimelineRef: React.RefObject<HTMLDivElement>;
  lastTimelineRef: React.RefObject<HTMLDivElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  className?: string;
}

export const PayrollPeriodTimelineLine = ({
  periodCount,
  firstTimelineRef,
  lastTimelineRef,
  containerRef,
  className,
}: PayrollPeriodTimelineLineProps) => {
  const [lineTop, setLineTop] = useState<string>("2rem");
  const [lineBottom, setLineBottom] = useState<string>("2rem");

  // Calculate line top and bottom positions based on first and last dot centers
  useEffect(() => {
    if (
      !firstTimelineRef.current ||
      !lastTimelineRef.current ||
      !containerRef.current ||
      periodCount === 0
    ) {
      return;
    }

    // Extract calculation into named function
    const updateLinePosition = () => {
      if (
        !firstTimelineRef.current ||
        !lastTimelineRef.current ||
        !containerRef.current
      ) {
        return;
      }

      const containerRect = containerRef.current.getBoundingClientRect();
      const firstTimelineRect =
        firstTimelineRef.current.getBoundingClientRect();
      const lastTimelineRect = lastTimelineRef.current.getBoundingClientRect();

      // Dot is 16px tall (h-4), center is 8px from top of dot
      const firstDotCenterY = firstTimelineRect.top - containerRect.top + 8; // 8px to center of 16px dot
      const lastDotCenterY = lastTimelineRect.top - containerRect.top + 8; // 8px to center of 16px dot

      const containerHeight = containerRect.height;
      setLineTop(`${firstDotCenterY}px`);
      const bottomOffset = containerHeight - lastDotCenterY;
      setLineBottom(`${bottomOffset}px`);
    };

    // Initial calculation
    updateLinePosition();

    // Set up ResizeObserver to watch for layout changes
    const observer = new ResizeObserver(() => {
      updateLinePosition();
    });

    // Observe the container for any size changes
    observer.observe(containerRef.current);

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, [periodCount]);

  if (periodCount <= 1) {
    return null;
  }

  return (
    <div
      className={cn(
        "absolute left-[16px] w-px bg-stroke-interactive",
        className,
      )}
      style={{
        top: lineTop,
        bottom: lineBottom,
      }}
    />
  );
};
